import * as React from 'react'
import styled from '@emotion/styled'
import { Tab, Tabs, TabProps } from '@mui/material'
import { mixins, Primary } from '@sphinx/components-styles'

export interface TabPanelWrapperProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  index: number
}

export interface TabData {
  label: string
  panel: React.ReactElement
  id?: string
}

export interface TabsPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: TabData[]
  initialTab?: number
}

export interface TabsPanelHandle {
  selectTab: (index: number) => void
  selectTabById: (id: string) => void
}

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)`
  &.MuiTab-root {
    ${mixins.button}
    text-transform: none;
    min-width: 0;
    color: ${Primary.navy[100]};
    opacity: 1;
    &:hover {
      color: ${Primary.orange[100]};
    }
    &.Mui-selected {
      color: ${Primary.orange[100]};
      &.Mui-focusVisible {
        color: ${Primary.navy[100]};
        background-color: ${Primary.orange[100]};
      }
    }
    &.Mui-focusVisible {
      background-color: ${Primary.orange[100]};
    }
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: transparent;
  & .MuiTabs-indicator {
    background-color: ${Primary.orange[100]};
  }
`

export const TabPanelWrapper: React.FC<TabPanelWrapperProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && children}
  </div>
)

const TabsPanel = React.forwardRef<TabsPanelHandle, TabsPanelProps>(
  ({ tabs, initialTab = 0, ...other }, ref) => {
    const [tabValue, setTabValue] = React.useState(initialTab)
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) =>
      setTabValue(newValue)

    React.useImperativeHandle(ref, () => ({
      selectTab: (index: number) => {
        setTabValue(index)
      },
      selectTabById: (id: string) => {
        const index = tabs.findIndex((tab) => tab.id === id)
        if (index !== -1) {
          setTabValue(index)
        }
      },
    }))

    return (
      <div {...other}>
        <StyledTabs value={tabValue} onChange={handleTabChange}>
          {tabs.map((tab, i) => (
            <StyledTab
              id={tab.id || `tab-${tab?.label?.split(' ').join('-')}-${i}`}
              key={`label-${tab.label}-${i}`}
              label={tab.label}
              aria-controls={`tabpanel-${i}`}
              wrapped
            />
          ))}
        </StyledTabs>
        {tabs.map((tab, i) => (
          <TabPanelWrapper
            key={`panel-${tab.label}-${i}`}
            value={tabValue}
            index={i}
          >
            {tab.panel}
          </TabPanelWrapper>
        ))}
      </div>
    )
  }
)

TabsPanel.displayName = 'TabsPanel'

export { TabsPanel }
